import PopperJs, { Placement } from '@popperjs/core'
import { Dispatch, FunctionComponent, SetStateAction, useEffect, useRef, useState } from 'react'
import { usePopper } from 'react-popper'
import styled from 'styled-components'

import { useElement } from 'services/hooks'
import usePreventScroll from 'services/hooks/usePreventScroll'

const MainContainer = styled.div``

const ContentContainer = styled.div``

const DropdownContainer = styled.div`
  z-index: ${({ theme }) => theme.zIndices.level7};
`

interface IProps {
  visible: boolean
  setVisible: Dispatch<SetStateAction<boolean>>
  yOffset?: number
  xOffset?: number
  placement?: Placement
  referenceElement: HTMLDivElement | null
}

const Dropdown: FunctionComponent<IProps> = ({
  visible,
  setVisible,
  referenceElement,
  yOffset,
  xOffset,
  placement,
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  const [contentRef, contentElement] = useElement<HTMLDivElement>()

  const { reenableScroll } = usePreventScroll(visible, contentElement, 'marketConditions')

  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)

  const popperOptions: Partial<PopperJs.Options> = {}

  if (yOffset || xOffset) {
    popperOptions.modifiers = [
      {
        name: 'offset',
        options: {
          offset: [xOffset || 0, yOffset || 0],
        },
      },
    ]
  }

  if (placement) {
    popperOptions.placement = placement
  }

  const { styles, attributes } = usePopper(referenceElement, popperElement, popperOptions)

  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement

    /*
      popper.js takes callback ref and popperElement returns null
      thus wrapper ref is used to detect outsie clicks beisdes toggling of the info icon
     */
    if (ref && ref?.current?.contains(target)) {
      // clicked on popover element
      setVisible(true)
    } else if (referenceElement && referenceElement.contains(target)) {
      // clicked on info icon
      setVisible(state => !state)
    } else {
      // clicked outside
      reenableScroll()
      setVisible(false)
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick)
    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [referenceElement])

  if (!visible) return null

  return (
    <MainContainer ref={contentRef}>
      <ContentContainer ref={ref}>
        <DropdownContainer
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          {children}
        </DropdownContainer>
      </ContentContainer>
    </MainContainer>
  )
}

export default Dropdown
